import { useState, useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { TabBar } from "./TabBar";
import { TopBar } from "./TopBar";
import { cn } from "@/lib/utils";

export function AppShell({ children }: { children: ReactNode }) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const loc = useLocation();

  // Close on route change
  useEffect(() => {
    setMobileNavOpen(false);
  }, [loc.pathname]);

  // Close on ESC
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileNavOpen]);

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-canvas text-foreground">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex">
        <Sidebar />
      </div>

      {/* Mobile drawer */}
      <div
        className={cn(
          "lg:hidden fixed inset-0 z-40 transition-opacity duration-200",
          mobileNavOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
        )}
        aria-hidden={!mobileNavOpen}
      >
        <div className="absolute inset-0 bg-black/50" onClick={() => setMobileNavOpen(false)} />
        <div
          className={cn(
            "absolute left-0 top-0 bottom-0 w-[280px] max-w-[85vw] shadow-lifted transition-transform duration-200 ease-out",
            mobileNavOpen ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <Sidebar onNavigate={() => setMobileNavOpen(false)} />
        </div>
      </div>

      <div className="relative flex-1 flex flex-col min-w-0">
        <div className="absolute inset-0 bg-grain-overlay opacity-40 pointer-events-none mix-blend-multiply" />
        <TopBar onOpenMobileNav={() => setMobileNavOpen(true)} />
        <TabBar />
        <main className="flex-1 overflow-y-auto relative">
          <div className="animate-tab-enter p-3 sm:p-4 lg:p-6 max-w-[1600px] mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
